<script setup>
import FuturesEmailVerify from './FuturesEmailVerify.vue'
import { addAddress } from '#common/services/addressServices'
import {
  placeFuturesOrder,
  updateFuturesShippingAddress,
} from '#common/services/checkoutServices'
import { pageOffset, scrollToError } from '#common/utils/form'
import { validatePhoneNumber } from '#common/utils/phone'
import { pushPageView } from '#common/utils/user'
import isMobile from 'ismobilejs'

const futuresForm = ref(null)
const { $axios, $store, $auth } = useNuxtApp()
const route = useRoute()
const emit = defineEmits(['order-placed'])
const shippingBlockOpen = ref(false)
const paymentBlockOpen = ref(false)
const showMobilePlaceOrderFooter = ref(true)
const verificationEmail = ref('')
const formValues = ref({})
const smsState = ref({})
const receive_shipping_updates_by_sms = ref(false)
const makeDefault = ref(false)
const show_sms_checkbox = ref(true)
const email_subscription_status = ref(true)
const userAddresses = ref([])
const profilePreferences = ref({
  email_subscription_frequency: 2, // weekly
})
const isPlacingOrder = ref(false)
const addressId = ref(null)
const inputErrors = ref({})
const errors = ref([])
const braintreeStore = useBraintree()
const newAddressSubmitErrors = ref([])
const hasNonDtcStateAddress = ref(false)
const selectedPaymentOption = ref(null)
const selectedPaymentMethod = ref(null)
const selectedMethodDetails = ref(null)
const futuresCart = computed(() => $store.getters.futuresCart)
const loggedIn = computed(() => $auth.loggedIn)
const metadata = useMetadata()
const layout = useLayout()
const mobile = isMobile(window.navigator).phone
const stateOptions = computed(() => {
  return Object.keys(metadata.stateNamesToIds).reduce((acc, curr) => {
    acc[curr] = curr
    return acc
  }, {})
})
const selectedPaymentMethodSummaryString = computed(() => {
  // Use the new ref as the primary source of truth
  if (selectedMethodDetails.value) {
    const obj = selectedMethodDetails.value
    if (
      selectedPaymentOption.value === 'paymentOptionCreditCard' ||
      obj.one_time
    ) {
      return `${obj.summary_string} expires ${obj.expiration_date}`
    } else {
      return obj.summary_string
    }
  }
  return ''
})
const showAddressInputs = computed(() => {
  return userAddresses.value.length === 0 || addressId.value === -1
})
const selectedAddress = computed(() => {
  if (
    !addressId.value ||
    !userAddresses.value ||
    userAddresses.value.length === 0
  )
    return null
  return userAddresses.value.find((item) => item.id === addressId.value)
})
const addressInForm = computed(() => {
  const addressData = {
    line1: formValues.value.line1,
    line2: formValues.value.line2,
    line3: formValues.value.line3,
    postcode: formValues.value.postcode,
    city: formValues.value.city || formValues.value.line4,
    phone_number: formValues.value.sms_number,
    state: formValues.value.state_name,
    is_default_for_shipping: makeDefault.value,
  }
  return addressData
})
const selectedStateCode = computed(() => {
  let stateCode = null
  if (showAddressInputs.value && formValues.value.state_name) {
    stateCode = metadata.stateNamesToCodes[formValues.value.state_name]
  } else if (selectedAddress.value) {
    stateCode = selectedAddress.value.state_code
  }
  return stateCode
})
const showPassageOfTitleWarning = computed(() => {
  if (selectedStateCode.value) {
    return metadata.settingsVars.COMPLIANCE_STATE_GROUPS.passage_of_title.includes(
      selectedStateCode.value,
    )
  }
  return false
})
const showCaliforniaWarning = computed(() => {
  return showPassageOfTitleWarning.value || selectedStateCode.value === 'CA'
})
const showMakeDefaultCheckbox = computed(() => {
  return userAddresses.value.length > 1
})
const showEmailVerification = computed(() => {
  return !loggedIn.value && !!verificationEmail.value
})
const nonDtcStates = computed(() => {
  return metadata.settingsVars.COMPLIANCE_STATE_GROUPS.non_dtc
    .concat(metadata.settingsVars.COMPLIANCE_STATE_GROUPS.private_label_only)
    .filter((s) => s !== 'PR')
    .sort()
    .join(', ')
})
const customPaymentTheme = computed(() => {
  return {
    showPlaceholders: true,
    cardholderName: {
      classes: '',
    },
    billingSameAsShippingCheckbox: {
      classes: 'checkmark-form-input',
    },
    savePaymentDetailsCheckbox: {
      classes: 'checkmark-form-input',
    },
    makeDefaultCheckbox: {
      classes: 'checkmark-form-input',
    },
    billingAddress: {
      line1: {
        classes: '',
      },
      line2: {
        classes: '',
      },
      city: {
        classes: 'medium-4',
      },
      state: {
        classes: 'medium-4',
      },
      zip: {
        classes: '',
      },
    },
  }
})

watch(loggedIn, async (newVal) => {
  if (newVal) {
    braintreeStore.resetBraintree()
    // Re fetch the cart in order to get the user addresses off the new merged cart
    await fetchCart()
    await updateUserInfo()
  }
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  if (loggedIn.value) updateUserInfo()
})
onActivated(() => {
  if (loggedIn.value) updateUserInfo()
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
async function fetchCart() {
  await $store.dispatch('getFuturesCart')
}
function handleScroll(event) {
  const mobilePlaceOrderFooter = document.getElementsByClassName(
    'futures-place-order-footer',
  )[0]
  const mobilePlaceOrderLandmark = document.getElementsByClassName(
    'futures-place-order-landmark',
  )[0]

  if (mobilePlaceOrderFooter && mobilePlaceOrderLandmark) {
    const showPlaceOrderFooter =
      pageOffset(mobilePlaceOrderLandmark) >= pageOffset(mobilePlaceOrderFooter)
    nextTick(() => {
      showMobilePlaceOrderFooter.value = showPlaceOrderFooter
    })
  }
}
async function updateUserInfo() {
  await fetchCart()
  fetchUserAddresses()
  await fetchSmsState()
  prepopulateFormData()
  shippingBlockOpen.value = !addressId.value
  paymentBlockOpen.value = !selectedPaymentMethod.value
  handleScroll()
}
async function fetchUserAddresses() {
  const nonDtcStates =
    metadata.settingsVars.COMPLIANCE_STATE_GROUPS.non_dtc.concat(
      metadata.settingsVars.COMPLIANCE_STATE_GROUPS.private_label_only,
    )
  const allAddresses = futuresCart.value.user_addresses.map((a) => {
    if (a.is_default_for_shipping) {
      addressId.value = a.id
    }
    return a
  })
  const nonDtcUserAddresses = allAddresses.filter(
    (a) => !nonDtcStates.includes(a.state_code),
  )
  hasNonDtcStateAddress.value = nonDtcUserAddresses.length < allAddresses.length
  userAddresses.value = nonDtcUserAddresses
  const selectedAddress = userAddresses.value.find(
    (x) => x.id === addressId.value,
  )
  if (!selectedAddress && addressId.value && userAddresses.value.length > 0)
    // If the default shipping address is not valid, select the first available user address
    addressId.value = userAddresses.value[0].id
}
async function fetchSmsState() {
  const resp = (await $axios.get('/api/customer/profile/')).data
  show_sms_checkbox.value = !resp.receive_shipping_updates_by_sms
  smsState.value.receive_shipping_updates_by_sms =
    resp.receive_shipping_updates_by_sms
  smsState.value.sms_number = resp.sms_number
  profilePreferences.value.email_subscription_status =
    resp.email_subscription_frequency !== null
  profilePreferences.value.email_subscription_frequency =
    resp.email_subscription_frequency
}
function prepopulateFormData() {
  if (smsState.value) {
    if (smsState.value.sms_number) {
      formValues.value.sms_number = smsState.value.sms_number
    }
    receive_shipping_updates_by_sms.value =
      smsState.value.receive_shipping_updates_by_sms
  }
  if (loggedIn.value) {
    email_subscription_status.value =
      profilePreferences.value.email_subscription_status
  }
}
function showError() {
  scrollToError(nextTick)
}
function registrationSuccess(payload = {}) {
  verificationEmail.value = payload.verificationEmail || $auth.authEmail || ''
  window.scrollTo(0, 0)
}
function autoCompleteAddress(autocomplete) {
  Object.assign(formValues.value, autocomplete)
}
function validateForm() {
  if (showAddressInputs.value) {
    const fieldErrors = {}
    const requiredFields = {
      first_name: 'First Name is required.',
      last_name: 'Last Name is required.',
      line1: 'Address is required.',
      line4: 'City is required.',
      state_name: 'State is required.',
      postcode: 'Zip/Postal Code is required.',
    }

    // Check for empty required fields
    for (const fieldName in requiredFields) {
      if (!formValues.value[fieldName]) {
        fieldErrors[fieldName] = requiredFields[fieldName]
      }
    }

    // Special check for the phone number
    if (!formValues.value.sms_number) {
      fieldErrors.sms_number = 'Error: Please enter a phone number'
    } else if (!validatePhoneNumber(formValues.value.sms_number, false)) {
      fieldErrors.sms_number =
        'Error: Phone number is incorrect, please use this format: (XXX)-XXX-XXXX'
    }

    // If we found any errors, display them and stop
    if (Object.keys(fieldErrors).length > 0) {
      futuresForm.value.node.setErrors([], fieldErrors)
      scrollToError(nextTick)
      return
    }
  }
}
async function placeOrder(data) {
  validateForm()
  if (isPlacingOrder.value) return
  isPlacingOrder.value = true
  try {
    const $route = useRoute()
    errors.value = []
    inputErrors.value = {}

    // Payment method validation
    // If no payment method is selected, show error and prevent submission
    if (!selectedPaymentMethod.value) {
      // Check which tab is selected and show error accordingly
      if (
        selectedPaymentOption.value === 'paymentOptionCreditCard' ||
        selectedPaymentOption.value === 'paymentOptionApplePay'
      ) {
        errors.value.push('Please select a credit card.')
      } else if (selectedPaymentOption.value === 'paymentOptionPayPal') {
        errors.value.push('Please select a PayPal method.')
      } else {
        errors.value.push('Please select a payment method.')
      }
    }

    if (errors.value.length > 0) {
      showError()
      return
    }
    // Stash sms number, as it gets cleared when address form is submitted.
    const smsNumber = formValues.value.sms_number

    if (addressId.value === null || addressId.value === -1) {
      const addressData = {
        first_name: formValues.value.first_name,
        last_name: formValues.value.last_name,
        date_of_birth: $auth.user.date_of_birth,
        line1: formValues.value.line1,
        line2: formValues.value.line2,
        line3: formValues.value.line3,
        line4: formValues.value.line4,
        postcode: formValues.value.postcode,
        phone_number: smsNumber,
        state: metadata.stateNamesToIds[formValues.value.state_name],
        is_default_for_shipping: makeDefault.value,
        skip_address_verification: true, // TODO
      }
      try {
        const newAddress = (await addAddress($axios, addressData)).data
        userAddresses.value.push(newAddress)
        addressId.value = newAddress.id
      } catch (e) {
        if (e.response.data) {
          const errorData = e.response.data
          // Use the ref to access the form's node and set errors
          if (futuresForm.value?.node && typeof errorData === 'object') {
            futuresForm.value.node.setErrors([], errorData)
          } else if (Array.isArray(errorData)) {
            // Fallback for general, non-field-specific errors
            newAddressSubmitErrors.value = errorData
          }
          scrollToError(nextTick)
        }
        Sentry.captureException(e)
        return
      }
    }
    if (futuresCart.value.selected_shipping_address !== addressId.value) {
      await updateFuturesShippingAddress($axios, addressId.value)
    }
    const extraData = {}
    if (show_sms_checkbox.value && receive_shipping_updates_by_sms.value) {
      extraData.receive_shipping_updates_by_sms =
        receive_shipping_updates_by_sms.value
      extraData.sms_number = receive_shipping_updates_by_sms.value
        ? smsNumber
        : null
    }
    let placedOrder
    try {
      placedOrder = await placeFuturesOrder(
        $axios,
        $route,
        futuresCart.value.id,
        extraData,
      )
    } catch (e) {
      if (e.response && e.response.data) {
        const data = e.response.data
        if (Array.isArray(data)) {
          errors.value = data
        } else if (typeof data === 'object' && data !== null) {
          Object.keys(data).forEach((key) => {
            const value = data[key]
            errors.value = errors.value.concat(
              _.map(Array.isArray(value) ? value : [value], (v) =>
                key !== 'non_field_errors' ? `${v} [${key}]` : v,
              ),
            )
          })
        }
      } else {
        errors.value = ['Error attempting to place order']
        console.log('error placing futures order', e)
      }
      // Refresh futures cart since the id might be different due to error handling in the backend
      await $store.dispatch('getFuturesCart')
      return
    }
    pushPageView($axios, {
      name: 'en-primeur-checkout-thank-you',
      properties: {
        path: $route.fullPath,
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
      },
    })
    emit('order-placed', placedOrder)
  } finally {
    isPlacingOrder.value = false
  }
}
function validPhoneNumber({ value }) {
  return validatePhoneNumber(value, false)
}
function selectedPaymentOptionChanged(newSelectedPaymentOption) {
  selectedPaymentOption.value = newSelectedPaymentOption
}
function selectedPaymentMethodChanged(methodObject) {
  if (methodObject) {
    selectedPaymentMethod.value = methodObject.card_id // Keep the ID ref updated
    selectedMethodDetails.value = methodObject // Store the full object
    paymentBlockOpen.value = !methodObject.card_id

    // Remove all payment method errors as soon as a method is selected
    errors.value = errors.value.filter(
      (err) =>
        err !== 'Please select a credit card.' &&
        err !== 'Please select a PayPal method.' &&
        err !== 'Please select a payment method.',
    )
  } else {
    // Handle deselection
    selectedPaymentMethod.value = null
    selectedMethodDetails.value = null
    paymentBlockOpen.value = true
  }
}
</script>

<template>
  <div>
    <div class="redemption-content checkout">
      <div class="row">
        <div class="columns redemption-content-sidebar">
          <div class="standalone-card">
            <h2 class="h2">Your En Primeur Order</h2>
            <FuturesCartContent :futures-cart="futuresCart" v-bind="$attrs" />
            <div class="order-total">
              <div>
                <div class="row total">
                  <div class="columns small-6 key">Order total</div>
                  <div class="columns small-6 val">
                    <Price :amt="Number(futuresCart.grand_total)" />
                  </div>
                </div>
              </div>
              <div class="row">
                <ErrorList :errors="errors" />
              </div>
              <div v-if="loggedIn" class="row">
                <div class="columns">
                  <button class="primary left block" @click="placeOrder">
                    Place Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns redemption-content-main">
          <!-- Checkout Header -->
          <div class="row">
            <div class="checkout-header columns">
              <h2>En Primeur Checkout</h2>
            </div>
          </div>
          <div class="row">
            <div class="columns">
              <div
                v-if="!mobile"
                class="checkout-desc"
                v-html="metadata.constance.EP1_CHECKOUT_DESCRIPTION"
              ></div>
            </div>
          </div>
          <!-- FormKit for address and other info -->
          <FormKit
            ref="futuresForm"
            v-model="formValues"
            type="form"
            :actions="false"
            name="futures-form"
            class="float-form"
            @submit.prevent
          >
            <div v-if="!loggedIn">
              <div class="row">
                <div class="columns medium-6">
                  <p>
                    Already a Wine Access member?
                    <a
                      class="underline"
                      href="#"
                      @click.prevent="
                        $auth.openAuthModal({ redirect: route.fullPath })
                      "
                    >
                      Sign In</a
                    >.
                  </p>
                </div>
              </div>
              <h2 v-if="!showEmailVerification">Create Your Account</h2>
              <FuturesEmailVerify
                v-if="showEmailVerification"
                :verification-email="verificationEmail"
              />
              <FuturesRegistration
                v-else
                id="ep-registration-section"
                :start-open="true"
                @registration-success="registrationSuccess"
              />
            </div>
            <div v-else>
              <div class="row">
                <div class="columns">
                  <div
                    class="ep-block"
                    :class="{ 'block-opened': shippingBlockOpen }"
                  >
                    <div
                      class="block-set-open"
                      @click="shippingBlockOpen = true"
                    >
                      Change
                    </div>
                    <h2>Shipping Address</h2>
                    <p
                      v-if="loggedIn && hasNonDtcStateAddress"
                      class="form-instructions adjust-margin"
                    >
                      Due to state law we cannot ship to {{ nonDtcStates }}.
                    </p>
                    <div v-if="selectedAddress" class="block-summary">
                      {{
                        selectedAddress.summary_string.replace(
                          ' (Shipping Default)',
                          '',
                        )
                      }}
                    </div>
                    <div class="block-content">
                      <div
                        v-if="userAddresses.length >= 1"
                        class="row"
                        style="margin: 0"
                      >
                        <div class="radio-grid stacked small">
                          <ul>
                            <li
                              v-for="address in userAddresses"
                              :key="address.id"
                              @click="shippingBlockOpen = false"
                            >
                              <input
                                :id="`shipping-address-${address.id}`"
                                v-model="addressId"
                                type="radio"
                                name="shipping_address_id"
                                :value="address.id"
                              />
                              <label :for="`shipping-address-${address.id}`">
                                <strong>{{
                                  address.summary_string.replace(
                                    ' (Shipping Default)',
                                    '',
                                  )
                                }}</strong>
                                <span v-if="address.is_default_for_shipping">
                                  (Shipping Default)
                                </span>
                              </label>
                            </li>
                            <li>
                              <input
                                id="shipping-address-new"
                                v-model="addressId"
                                type="radio"
                                name="shipping_address_id"
                                :value="-1"
                              />
                              <label for="shipping-address-new">
                                <strong> Add a new shipping address </strong>
                              </label>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div v-if="showAddressInputs">
                        <div class="row">
                          <div class="columns medium-6">
                            <div class="floating">
                              <FloatingInputFK
                                id="recipient-firstname"
                                name="first_name"
                                :validation="
                                  showAddressInputs ? 'required' : ''
                                "
                                placeholder="First Name"
                                label="First Name"
                              />
                            </div>
                          </div>
                          <div class="columns medium-6">
                            <div class="floating">
                              <FloatingInputFK
                                id="recipient-lastname"
                                name="last_name"
                                :validation="
                                  showAddressInputs ? 'required' : ''
                                "
                                placeholder="Last Name"
                                label="Last Name"
                              />
                            </div>
                          </div>
                          <div class="columns small-12">
                            <div class="floating">
                              <FloatingInputFK
                                id="recipient-company"
                                name="line3"
                                placeholder="Company Name (Optional)"
                                label="Company Name (Optional)"
                              />
                            </div>
                          </div>
                          <GiftingAddressInputs
                            v-if="showAddressInputs"
                            id-prefix="ep"
                            :state-options="stateOptions"
                            :state-codes="metadata.stateNamesToCodes"
                            :allow-alaska-hawaii="true"
                            @addressAutocomplete="autoCompleteAddress"
                          />
                        </div>
                        <div v-if="show_sms_checkbox" class="row">
                          <div class="columns medium-6">
                            <div class="floating">
                              <FloatingInputFK
                                id="recipient-phone"
                                name="sms_number"
                                placeholder="Phone"
                                label="Phone"
                                validation="required|validPhoneNumber"
                                :validation-rules="{
                                  validPhoneNumber,
                                }"
                                :validation-visibility="dirty"
                                :validation-messages="{
                                  required:
                                    'Error: Please enter a phone number',
                                  validPhoneNumber:
                                    'Error: Phone number is incorrect, please use this format: (XXX)-XXX-XXXX',
                                }"
                              />
                            </div>
                            <div class="checkmark-form-input">
                              <input
                                id="receive_shipping_updates_by_sms"
                                v-model="receive_shipping_updates_by_sms"
                                type="checkbox"
                                name="receive_shipping_updates_by_sms"
                              />
                              <label for="receive_shipping_updates_by_sms">
                                Text me delivery updates
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="showMakeDefaultCheckbox" class="row">
                        <div class="columns medium-6">
                          <div class="checkmark-form-input">
                            <input
                              id="address-makeDefaultCheckbox"
                              v-model="makeDefault"
                              type="checkbox"
                            />
                            <label for="address-makeDefaultCheckbox">
                              This is my default shipping address
                            </label>
                          </div>
                        </div>
                      </div>
                      <div v-if="showAddressInputs" class="row">
                        <ErrorList :errors="newAddressSubmitErrors" />
                      </div>
                      <p class="form-instructions adjust-margin">
                        Adult signature required for delivery.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FormKit>

          <!-- Payment Section -->
          <div v-if="loggedIn" class="row">
            <div class="columns">
              <div
                class="ep-block"
                :class="{ 'block-opened': paymentBlockOpen }"
              >
                <div class="block-set-open" @click="paymentBlockOpen = true">
                  Change
                </div>
                <h2>Payment</h2>
                <div v-if="selectedPaymentMethod" class="block-summary">
                  {{ selectedPaymentMethodSummaryString }}
                </div>
                <div class="block-content checkout-form">
                  <PaymentTabInputs
                    :new-c-c-form-styles="customPaymentTheme"
                    :shipping-address="
                      selectedAddress ? selectedAddress : addressInForm
                    "
                    :total="Number(futuresCart.grand_total)"
                    @selectedPaymentOptionChanged="selectedPaymentOptionChanged"
                    @selectedPaymentMethodChanged="selectedPaymentMethodChanged"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="row cart-content">
            <FuturesCartContent
              :futures-cart="futuresCart"
              v-bind="$attrs"
              class="ep-block"
            />
          </div>

          <!-- Order Total & Place Order Button -->
          <div class="order-total-bottom">
            <div v-if="loggedIn">
              <div class="row">
                <div class="columns">
                  <div class="order-total">
                    <div>
                      <div class="row total">
                        <div class="columns small-6 key">Order total</div>
                        <div class="columns small-6 val">
                          <Price :amt="Number(futuresCart.grand_total)" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <ErrorList :errors="errors" />
              </div>
              <div class="futures-place-order-landmark"></div>
              <div v-if="loggedIn" class="row">
                <div class="columns">
                  <button class="primary left block" @click="placeOrder">
                    Place Order
                  </button>
                </div>
              </div>
            </div>
            <div v-if="loggedIn" class="row">
              <div class="columns">
                <div class="terms-and-conditions">
                  By clicking place order I understand and agree to the
                  <a
                    href="#"
                    class="underline"
                    @click.prevent="layout.showTermsAndConditions = true"
                  >
                    Terms and Conditions</a
                  >.
                </div>
                <div
                  v-if="mobile"
                  class="checkout-desc disclaimer"
                  v-html="metadata.constance.EP1_CHECKOUT_DESCRIPTION"
                ></div>
                <div
                  class="disclaimer"
                  v-html="metadata.constance.EP1_CHECKOUT_DISCLAIMER"
                />
                <div v-if="showPassageOfTitleWarning" class="disclaimer">
                  <p>
                    By clicking place order I understand and agree that title to
                    the wine passes to me in California upon completion of my
                    purchase and authorize Wine Access to allow an independent
                    fulfillment provider to fulfill my order according to my
                    directions.
                  </p>
                </div>
                <div v-if="showCaliforniaWarning" class="disclaimer">
                  <p>
                    <strong>WARNING:</strong>
                    Many food and beverage cans have linings containing
                    bisphenol A (BPA), a chemical known to the State of
                    California to cause harm to the female reproductive system.
                    Jar lids and bottle caps may also contain BPA. You can be
                    exposed to BPA when you consume foods or beverages packaged
                    in these containers. For more information go to
                    <a href="http://www.P65Warnings.ca.gov" target="_blank"
                      >www.P65Warnings.ca.gov</a
                    >
                    . For a list of products go to:
                    <a href="http://www.prop65bpa.org" target="_blank"
                      >www.prop65bpa.org</a
                    >
                    .
                  </p>
                  <p>
                    <strong>WARNING:</strong>
                    Drinking distilled spirits, beer, coolers, wine and other
                    alcoholic beverages may increase cancer risk, and, during
                    pregnancy, can cause birth defects. For more information go
                    to www.P65Warnings.ca.gov/alcohol.
                  </p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="columns">
                <CustomerServiceFooter
                  customer-service-email-override="ep@wineaccess.com"
                />
              </div>
            </div>
          </div>
          <div
            v-if="loggedIn"
            :style="{
              visibility: showMobilePlaceOrderFooter ? 'visible' : 'hidden',
            }"
            class="futures-place-order-footer"
          >
            <div class="order-total">
              <div>
                <div class="row total">
                  <div class="columns small-6 key">Order total</div>
                  <div class="columns small-6 val">
                    <Price :amt="Number(futuresCart.grand_total)" />
                  </div>
                </div>
              </div>
            </div>
            <button class="primary left block" @click="placeOrder">
              Place Order
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
